<template>
  <div class="payment-methods">
    <v-skeleton-loader
      v-if="isLoading"
      class="payment-skeleton"
      type="button, button, button" />

    <v-alert v-else-if="error" type="error" dense text>
      {{ error }}
    </v-alert>

    <div v-else-if="!paymentMethods.length" class="empty-state grey--text">
      {{
        $t('entities.paymentMethods.none') || 'No payment methods available.'
      }}
    </div>

    <div v-else class="payment-buttons">
      <v-btn
        v-for="method in paymentMethods"
        :key="method.id"
        class="payment-button"
        :class="{ active: isSelected(method.id) }"
        elevation="0"
        height="76"
        @click="selectMethod(method)">
        <v-icon :color="isSelected(method.id) ? 'white' : iconColor">
          {{ getIcon(method.code) }}
        </v-icon>
        <span
          class="payment-label"
          :class="{ 'label-active': isSelected(method.id) }">
          {{ getPaymentMethodShortName(method.code) }}
        </span>
      </v-btn>
    </div>
  </div>
</template>

<script>
import PaymentMethod, { PaymentMethodCode } from '@/models/shop/PaymentMethod'

export default {
  name: 'BasketPaymentMethods',
  props: {
    defaultMethodCode: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      paymentMethods: [],
      isLoading: false,
      error: '',
      selectedMethodId: '',
      hasUserSelected: false,
      iconColor: '#3b3b3b',
    }
  },
  computed: {
    arenaId() {
      return this.$store.getters.arena?.id || ''
    },
  },
  watch: {
    arenaId: {
      immediate: true,
      handler() {
        this.fetchPaymentMethods()
      },
    },
    defaultMethodCode() {
      if (this.hasUserSelected || this.isLoading) return
      if (!this.paymentMethods.length) return
      this.setDefaultSelection()
    },
  },
  methods: {
    getPaymentMethodShortName(methodCode) {
      return this.$t(`miscellaneous.paymentMethodsShortNames.${methodCode}`)
    },
    async fetchPaymentMethods() {
      if (!this.arenaId) {
        this.paymentMethods = []
        this.selectedMethodId = ''
        return
      }

      this.isLoading = true
      this.error = ''

      try {
        const response = await PaymentMethod.api.getArenaPaymentMethods(
          this.arenaId,
        )
        const responseData = this.unpackResponse(response)
        const methods = responseData.map((data) => new PaymentMethod(data))
        if (methods.length > 1) {
          const firstMethod = methods.shift()
          methods.push(firstMethod)
        }
        this.paymentMethods = methods
        this.setDefaultSelection()
      } catch (e) {
        console.error('Fetching payment methods failed.', e)
        this.paymentMethods = []
        this.selectedMethodId = ''
        this.error =
          e?.response?.data?.message ||
          this.$t('entities.paymentMethods.fetchError') ||
          'Unable to load payment methods.'
      } finally {
        this.isLoading = false
      }
    },
    unpackResponse(response) {
      const payload = response?.data?.data ?? response?.data ?? []
      if (Array.isArray(payload)) return payload
      if (Array.isArray(payload?.data)) return payload.data
      return []
    },
    setDefaultSelection() {
      if (!this.paymentMethods.length) {
        this.emitSelection(null)
        return
      }

      if (this.hasUserSelected) {
        const existingSelection = this.paymentMethods.find(
          (method) => method.id === this.selectedMethodId,
        )
        if (existingSelection) {
          this.emitSelection(existingSelection)
          return
        }
        this.hasUserSelected = false
      }

      // Default to the method configured in the arena's invoice settings,
      // falling back to Cash (CSH), then to the first available method
      const defaultMethod =
        this.paymentMethods.find(
          (method) => method.code === this.defaultMethodCode,
        ) ||
        this.paymentMethods.find(
          (method) => method.code === PaymentMethodCode.CSH,
        ) ||
        this.paymentMethods[0]
      this.selectedMethodId = defaultMethod.id
      this.emitSelection(defaultMethod)
    },
    selectMethod(method) {
      if (this.selectedMethodId === method.id) return
      this.hasUserSelected = true
      this.selectedMethodId = method.id
      this.emitSelection(method)
    },
    emitSelection(method) {
      this.$emit('payment-method-selected', method)
    },
    isSelected(id) {
      return this.selectedMethodId === id
    },
    getIcon(code) {
      switch (code) {
        case PaymentMethodCode.CRD:
          return 'mdi-credit-card-outline'
        case PaymentMethodCode.BNK:
          return 'mdi-bank-outline'
        case PaymentMethodCode.CSH:
        default:
          return 'mdi-cash-multiple'
      }
    },
  },
}
</script>

<style scoped>
.payment-methods {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.section-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #4b5563;
}

.payment-skeleton {
  max-width: 22.5rem;
}

.payment-buttons {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.payment-button {
  min-width: 6.75rem;
  width: 6.75rem;
  background: #e3e3e3;
  color: #2d2d2d;
  text-transform: none;
  letter-spacing: 0;
  font-weight: 600;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-shadow: none;
}

::v-deep .v-btn__content {
  flex-direction: column;
}
.payment-button .v-icon {
  margin-bottom: 0.375rem;
}

.payment-label {
  font-size: 0.9375rem;
}

.payment-button.active {
  background: var(--v-primary-base) !important;
  color: #ffffff;
}

.payment-button.active .payment-label,
.payment-button.active .v-icon {
  color: #ffffff !important;
}

::v-deep .theme--dark.payment-button .v-icon {
  color: #ffffff !important;
}

.empty-state {
  font-size: 0.875rem;
}
</style>
